import { computed, ref } from 'vue';
import type { ComputedRef } from 'vue';

import type { LowPrices } from '@aviata/platform-client';
import { CALENDAR_MODES } from '../config';
import type { CalendarMode } from '../config';
import { filterPricesByDate } from '../lib';

type UseSearchFormCalendarProps = {
  selectedDates: string[];
  selectedCalendarMode: ComputedRef<CalendarMode>;
  prices: ComputedRef<
    LowPrices.SearchCalendarResponse | Record<string, { price: string }>
  >;
  isNewCalendarVersion?: boolean;
};

export function useSearchFormCalendar({
  selectedDates,
  selectedCalendarMode,
  prices,
  isNewCalendarVersion = false,
}: UseSearchFormCalendarProps) {
  const draftDates = ref<string[]>(selectedDates);

  const hasSelectedDates = computed(
    () => draftDates.value.length > 0 && draftDates.value.every(Boolean),
  );

  const isActiveMode = (mode: CalendarMode) =>
    selectedCalendarMode.value === mode;

  const calendarPrices = computed(() => {
    if (!isNewCalendarVersion) {
      return draftDates.value.length > 1 || isActiveMode(CALENDAR_MODES.RANGE)
        ? (prices.value as LowPrices.SearchCalendarResponse)?.RT
        : (prices.value as LowPrices.SearchCalendarResponse)?.OW;
    }
    if (isActiveMode('single') || draftDates.value.length === 0) {
      return prices.value;
    }
    return filterPricesByDate(
      draftDates.value[0],
      prices.value as Record<string, { price: string }>,
    );
  });

  const clearSecondDate = () => {
    const [first] = draftDates.value;
    updateDates([first]);
  };

  function updateDates(dates: string[]) {
    draftDates.value = dates;
  }

  function resetDates() {
    draftDates.value = [];
  }

  return {
    hasSelectedDates,
    isActiveMode,
    calendarPrices,
    clearSecondDate,
    resetDates,
    updateDates,
    state: draftDates,
  };
}
